import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../hooks/redux';
import { usePermissions } from '../hooks/usePermissions';

interface PermissionGuardProps {
  /** Permission key required to render the wrapped page (e.g. `mobile_read`). */
  permission: string;
  children: React.ReactNode;
}

/**
 * Blocks a route for subusers missing `permission`. Rendered INSTEAD of the
 * page, so none of the page's data fetches fire. This is the UX layer only -
 * the backend still answers 403 `subuser_permission_denied` on direct calls,
 * which the global interceptor in `services/auth.ts` surfaces.
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({ permission, children }) => {
  const { t } = useTranslation();
  const isDataInitialized = useAppSelector((state) => state.auth.isDataInitialized);
  const { can } = usePermissions();

  if (!isDataInitialized) return null;

  if (!can(permission)) {
    return (
      <div className="dashboard-empty-state">
        <p className="dashboard-empty-state-text">{t('subuser_no_permission_title')}</p>
        <p className="dashboard-empty-state-hint">{t('subuser_no_permission_description')}</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default PermissionGuard;
